import { useCallback, useMemo } from 'react';
import { useStore } from 'zustand';
import { useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useBulkCreateDraftsEntry } from '~/components/BulkCreateDraftsWizard/hooks/useBulkCreateDraftsEntry';
import { useStoreActions } from '~/contexts';
import { QueryKeys } from '~/hooks/reactQuery/queryKeys';
import { useQueryState } from '~/hooks/reactQuery/useQueryState';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { taskFromJob } from '../taskModel';
import { useTasksDrawerStoreApi } from '../tasksDrawerStore';
const EMPTY = [];
function toAwaitedJob(task, overrides) {
    return {
        jobId: task.jobId,
        kind: task.kind,
        agreementName: task.agreementName,
        relationshipId: task.relationshipId,
        overrides
    };
}
export const GetTasksJobsTID = (parentTestId)=>getTestIds(parentTestId);
const SILENT_FEEDBACK = {
    kind: 'silent'
};
export function useTasksJobs() {
    let { refreshOnHookMount = false, enabled = true } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
    const { api } = useStoreActions();
    const { BULK_CREATE_PROPOSALS } = useFeatureFlags();
    const { isOffered: canOpenBulkRuns } = useBulkCreateDraftsEntry();
    const drawerStore = useTasksDrawerStoreApi();
    const openDrawer = useStore(drawerStore, (s)=>s.openDrawer);
    const { showInfoToast } = useOverlayContext();
    const { data, isLoading, isFetching, isFetched, refresh, optimisticUpdate, getQueryData: getCachedTasks, error } = useQueryState({
        queryKey: [
            QueryKeys.TasksJobs
        ],
        queryFn: async ()=>(await api.jobs.getJobs()).map(taskFromJob).filter((task)=>task != null),
        refreshOnHookMount,
        enabled
    });
    const addTask = useCallback(function(task) {
        let feedback = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : SILENT_FEEDBACK, overrides = arguments.length > 2 ? arguments[2] : void 0;
        optimisticUpdate((tasks)=>[
                task,
                ...tasks.filter((t)=>t.jobId !== task.jobId)
            ]);
        drawerStore.getState().registerAwaitedJob(toAwaitedJob(task, overrides));
        const { TasksJobsTestIds } = GetTasksJobsTID();
        const toastTestId = TasksJobsTestIds.InitialToast(task.jobId);
        switch(feedback.kind){
            case 'silent':
                return;
            case 'auto':
                if (drawerStore.getState().isTasksDrawerAvailable) openDrawer();
                else showInfoToast(feedback.message, toastTestId);
                return;
            default:
                {
                    const _exhaustive = feedback;
                    return _exhaustive;
                }
        }
    }, [
        optimisticUpdate,
        drawerStore,
        openDrawer
    ]);
    const removeTask = useCallback((jobId)=>{
        optimisticUpdate((tasks)=>tasks.filter((t)=>t.jobId !== jobId));
        drawerStore.getState().dropAwaitedJob(jobId);
    }, [
        optimisticUpdate,
        drawerStore
    ]);
    const visibleTasks = useCallback((list)=>{
        const shownKinds = {
            relationshipPdf: true,
            bulkSendProposals: !!BULK_CREATE_PROPOSALS,
            bulkCreateDrafts: canOpenBulkRuns
        };
        return list.filter((task)=>shownKinds[task.kind]);
    }, [
        BULK_CREATE_PROPOSALS,
        canOpenBulkRuns
    ]);
    const tasks = useMemo(()=>visibleTasks(data !== null && data !== void 0 ? data : EMPTY), [
        data,
        visibleTasks
    ]);
    const getTasks = useCallback(()=>{
        var _getCachedTasks;
        return visibleTasks((_getCachedTasks = getCachedTasks()) !== null && _getCachedTasks !== void 0 ? _getCachedTasks : EMPTY);
    }, [
        getCachedTasks,
        visibleTasks
    ]);
    return {
        tasks,
        getTasks,
        isLoading,
        isFetching,
        isSettled: isFetched,
        refresh,
        optimisticUpdate,
        addTask,
        removeTask,
        error
    };
}
